import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Signup from './pages/Signup';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import Candidates from './pages/Candidates';
import Pipeline from './pages/Pipeline';
import Interviews from './pages/Interviews';
import Clients from './pages/Clients';
import Users from './pages/Users';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import ActivityLogs from './pages/ActivityLogs';
import Workbench from './pages/Workbench';
import CommandCenter from './pages/CommandCenter';
import AdminTickets from './pages/AdminTickets';
import CtcCleanup from './pages/CtcCleanup';
import JobAllocation from './pages/JobAllocation';
import RecruiterPerformance from './pages/RecruiterPerformance';
import MyTeam from './pages/MyTeam';
import PlanLimitProvider from './components/PlanLimitProvider';
import './App.css';

// Error Boundary Component to catch runtime errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Application Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center p-8 max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Something went wrong</h2>
            <p className="text-slate-600 mb-4">We encountered an unexpected error. Please try refreshing the page.</p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const PrivateRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    const home = user?.role === 'recruiter' ? '/command-center' : '/dashboard';
    return <Navigate to={home} replace />;
  }

  return children;
};

// Helper component: redirect to role-aware home (or landing for guests)
const RoleAwareHome = () => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }
  if (!isAuthenticated) return <Landing />;
  const home = user?.role === 'recruiter' ? '/command-center' : '/dashboard';
  return <Navigate to={home} replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <PrivateRoute>
            <Jobs />
          </PrivateRoute>
        }
      />
      <Route
        path="/candidates"
        element={
          <PrivateRoute>
            <Candidates />
          </PrivateRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <PrivateRoute>
            <Pipeline />
          </PrivateRoute>
        }
      />
      <Route
        path="/interviews"
        element={
          <PrivateRoute>
            <Interviews />
          </PrivateRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <PrivateRoute>
            <Clients />
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute allowedRoles={['admin', 'team_lead', 'delivery_manager']}>
            <Users />
          </PrivateRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <PrivateRoute allowedRoles={['admin', 'delivery_manager', 'team_lead']}>
            <Reports />
          </PrivateRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <PrivateRoute allowedRoles={['admin', 'delivery_manager']}>
            <Analytics />
          </PrivateRoute>
        }
      />
      <Route
        path="/activity-logs"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <ActivityLogs />
          </PrivateRoute>
        }
      />
      <Route
        path="/workbench"
        element={
          <PrivateRoute allowedRoles={['admin', 'delivery_manager', 'team_lead', 'recruiter']}>
            <Workbench />
          </PrivateRoute>
        }
      />
      <Route
        path="/command-center"
        element={
          <PrivateRoute allowedRoles={['admin', 'delivery_manager', 'team_lead', 'recruiter']}>
            <CommandCenter />
          </PrivateRoute>
        }
      />
      {/* Back-compat for old bookmarks that pointed at /workbench-legacy
          during the transition window. Will be removed in a later cleanup. */}
      <Route path="/workbench-legacy" element={<Navigate to="/workbench" replace />} />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <PrivateRoute>
            <AdminTickets />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/ctc-cleanup"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <CtcCleanup />
          </PrivateRoute>
        }
      />
      <Route
        path="/job-allocation"
        element={
          <PrivateRoute allowedRoles={['admin', 'delivery_manager', 'team_lead']}>
            <JobAllocation />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-performance"
        element={
          <PrivateRoute allowedRoles={['admin', 'delivery_manager', 'team_lead', 'recruiter']}>
            <RecruiterPerformance />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-team"
        element={
          <PrivateRoute allowedRoles={['admin', 'delivery_manager', 'team_lead']}>
            <MyTeam />
          </PrivateRoute>
        }
      />

      {/* Default redirect (role-aware) */}
      <Route path="/" element={<RoleAwareHome />} />
      <Route path="*" element={<RoleAwareHome />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <PlanLimitProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </PlanLimitProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
